import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Chev } from "../shared/icons";

/*
 * Live detection panel for the /next hero.
 *
 * A framed camera feed that cycles through scenes, drawing bounding boxes on the features
 * actually visible in each one. Every scene is padded to the SAME 4:3 frame, so the
 * percentage box positions below stay valid as the feed switches. The plates are WebP: the
 * hero mounts five of them at once, and as JPEG that was 501 KB on the landing screen.
 *
 * The treatment — filled boxes, a glowing scan line, brackets on the frame, a dotted HUD
 * grid and the bottom status strip — mirrors the AiLanding viewfinder in aicamera_frontend
 * (components/home/AiLanding.tsx), so the sales kit and the product read as one system.
 *
 * Deliberate constraints:
 *  - Boxes sit on real features. Empty boxes over blank asphalt would read as fake.
 *  - `warn` (dashed amber) is only for things that ARE a defect in that picture — a crack, a
 *    pothole, an overflowing bin. Assets in good order keep the red "identified" treatment.
 *    A warn box on a clean panel is the same lie as a box over nothing.
 *  - A box may run behind the status strip at the bottom of the frame — that reads as the
 *    object continuing past the HUD — but a LABEL must never, so keep y above ~60%.
 *  - pointer-events: none and aria-hidden — decoration must never intercept a tap or reach
 *    a screen reader.
 *  - prefers-reduced-motion: no cycling, no scan line; the first scene rests with its boxes up.
 *  - It owns a fixed-aspect panel rather than floating over the hero, because an overlay put
 *    labels on top of the headline and clipped them at the viewport edge.
 */

const IMG = (f) => `${process.env.PUBLIC_URL}/camcom-next/scenes/${f}`;

/* x/y/w/h are % of the 4:3 frame. Keep labels clear of the top and right edges, keep boxes
   in a scene vertically apart or their labels collide. A box that runs under the status strip
   is fine; a label there is not. `flip` anchors a label to the box's right edge, `below`
   hangs it under the box — use it when the box is too near the top for a label above. */
const PAVEMENT = {
  id: "pavement", img: "pavement.webp",
  dets: [
    { id: "crack",   label: "co_det_crack",   conf: 0.91, x: 58, y: 46, w: 17, h: 12, warn: true, flip: true },
    { id: "pothole", label: "co_det_pothole", conf: 0.94, x: 41, y: 62, w: 13, h: 10, warn: true, flip: true },
  ],
};
const WASTE = {
  id: "waste", img: "waste.webp",
  dets: [
    { id: "bin",    label: "co_det_bin",    conf: 0.96, x: 37, y: 33, w: 13, h: 24, warn: true },
    { id: "debris", label: "co_det_debris", conf: 0.79, x: 52, y: 61, w: 14, h: 11, warn: true },
  ],
};
const SIGNAGE = {
  id: "signage", img: "signage.webp",
  dets: [
    { id: "signage", label: "co_det_signage", conf: 0.88, x: 55, y: 34, w: 20, h: 21 },
  ],
};

/* The whole street, in deck order. Kept as a named set because it is the Public Safety story
   told in one loop. */
export const STREET_SCENES = [PAVEMENT, WASTE, SIGNAGE];

/* Public Safety splits the same three plates by what each use case actually inspects, so the
   feed changes when you switch tabs: civic assets and cleanliness vs the carriageway. Signage
   belongs to both — it is street furniture and a road sign. These MUST stay module-level
   constants: the cycle effect keys on the `scenes` prop, and a new array each render restarts
   it forever. */
export const CITY_SCENES = [WASTE, SIGNAGE];
export const ROAD_SCENES = [PAVEMENT, SIGNAGE];

/* Motor: the plate the aicamera motor landing uses (aicamera_frontend public/front-left.png
   on v2-dev-ergobaltic), the car lifted off its white field and scaled to 80% of the frame so
   the whole vehicle sits inside the mobile panel with margin.
   Carries the landing's DENT? — a query on a defect, not an assertion, which is the honest way
   to show a flagged finding on a render with no damage in it. The landing's fourth box
   (PANEL · OK) is dropped: it fits geometrically, but four chips at this legibility size cover
   most of a car that now only spans 80% of a 284px panel, and the plate stops reading. */
const MOTOR = {
  id: "motor", img: "motor.webp",
  /* placement solved against 284x213 (a 320px viewport) through 600x450; margin >= 8.5px */
  dets: [
    { id: "headlight", label: "co_det_headlight", conf: 0.98, x: 11, y: 40, w: 9,  h: 8 },
    { id: "wheel",     label: "co_det_wheel",     conf: 0.95, x: 43, y: 55, w: 14, h: 17, flip: true },
    /* the defect lands last — it is the point of the sequence */
    { id: "dent",      label: "co_det_dent",      conf: 0.91, x: 60, y: 38, w: 12, h: 12, warn: true },
  ],
};
export const MOTOR_SCENES = [MOTOR];

/* Property: the exterior plate the aicamera property landing uses, re-framed to 4:3. The
   house is in good order, so these are element detections rather than findings. */
const PROPERTY = {
  id: "property", img: "property.webp",
  dets: [
    /* Crowded: three labels in a 284px frame at the narrowest supported width. The gable is
       too near the top for a label above it, and the door's label has to hang below or it
       lands on the roof's. Verified at 284x213 and 480x360. */
    { id: "roof",     label: "co_det_roof",     conf: 0.96, x: 59, y: 9,  w: 26, h: 12, below: true, flip: true },
    { id: "window",   label: "co_det_window",   conf: 0.94, x: 22, y: 44, w: 14, h: 15, flip: true },
    { id: "entrance", label: "co_det_entrance", conf: 0.97, x: 45, y: 48, w: 10, h: 14, below: true },
  ],
};
export const PROPERTY_SCENES = [PROPERTY];

/* The home hero is the breadth pitch — "show them what the cameras already see" — so it runs all
   five plates and DELIBERATELY alternates domains: a car, then a road, then a house. Grouping the
   three street scenes together would put motor and property 12s into a ~20s loop, where most
   viewers never reach them. */
export const HOME_SCENES = [MOTOR, PAVEMENT, PROPERTY, WASTE, SIGNAGE];

/* Which context gets a panel. Keyed "vertical|useCase"; absent = no panel. */
export const SCENE_SETS = {
  "insurance|motor": MOTOR_SCENES,
  "insurance|property": PROPERTY_SCENES,
  "safety|city": CITY_SCENES,
  "safety|road": ROAD_SCENES,
  /* "safety|construction" has no plate — see F20. A pothole feed under "Construction safety
     monitoring" would be the same lie as a box over blank asphalt, so it shows no panel. */
};

/*
 * Where each plate actually leads, so the panel can offer it.
 *
 * The home hero cycles five plates and the viewer has no way to act on the one in front of them
 * — they see a car, and still have to go and find Insurance. Each scene now carries the use case
 * it is a picture of, and the panel shows it as a control attached beneath the frame.
 *
 * Labels reuse the use-case short names, which are already translated in all eleven locales;
 * nothing new to key. SIGNAGE belongs to both City & civic and Road & tollways — it is street
 * furniture and a road sign — and is sent to City, which is the set that leads with it.
 */
export const SCENE_DEST = {
  motor:    { v: "insurance", u: "motor",    label: "co_uc_insurance_motor_short" },
  property: { v: "insurance", u: "property", label: "co_uc_insurance_property_short" },
  pavement: { v: "safety",    u: "road",     label: "co_uc_safety_road_short" },
  waste:    { v: "safety",    u: "city",     label: "co_uc_safety_city_short" },
  signage:  { v: "safety",    u: "city",     label: "co_uc_safety_city_short" },
};

const STAGGER = 380;   // ms between boxes appearing
const HOLD = 2400;     // ms the completed scene stays up
/* Must exceed the image crossfade (.55s in CSS) or the first box of a new scene lands
   while the previous scene is still visible, labelling the wrong picture. */
const SETTLE = 750;

const prefersReduced = () => {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
  catch { return false; }
};

/* counts 0 -> target once, when the box appears */
const Confidence = ({ value, still }) => {
  const [n, setN] = useState(still ? value : 0);
  const raf = useRef(0);
  useEffect(() => {
    if (still) { setN(value); return undefined; }
    const t0 = performance.now();
    const tick = (now) => {
      /* rAF hands you the FRAME's timestamp, which can predate the performance.now() taken
         when the effect ran — if a frame was already in flight. Unclamped that made p
         negative, (1-p)^3 > 1, and the label rendered a negative confidence ("-0.01"). */
      const p = Math.min(1, Math.max(0, (now - t0) / 650));
      setN(value * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [value, still]);
  return <b>{n.toFixed(2)}</b>;
};

const DetectionOverlay = ({ scenes = HOME_SCENES, onPick }) => {
  const { t } = useTranslation();
  const still = prefersReduced();
  const [scene, setScene] = useState(0);
  /* The scene the viewer can actually SEE — `scene` leads it by the crossfade. */
  const [shown, setShown] = useState(0);
  const [live, setLive] = useState(() => (still ? scenes[0].dets.map((d) => d.id) : []));

  useEffect(() => {
    if (still) return undefined;
    const timers = [];
    setLive([]);
    scenes[scene].dets.forEach((d, i) => {
      timers.push(setTimeout(() => {
        setLive((prev) => (prev.includes(d.id) ? prev : [...prev, d.id]));
      }, SETTLE + i * STAGGER));
    });
    timers.push(setTimeout(
      () => setScene((s) => (s + 1) % scenes.length),
      SETTLE + scenes[scene].dets.length * STAGGER + HOLD,
    ));
    return () => timers.forEach(clearTimeout);
  }, [scene, still, scenes]);

  useEffect(() => {
    if (still) { setShown(scene); return undefined; }
    const id = setTimeout(() => setShown(scene), SETTLE);
    return () => clearTimeout(id);
  }, [scene, still]);

  const current = scenes[scene];
  /* Only where a caller can act on it — the vertical screens already ARE the destination, so
     they pass no onPick and get no control.

     Keyed on `shown`, not `scene`. The image crossfades over .55s while the boxes wait SETTLE
     before drawing, so for the first 750ms of a switch the previous plate is still on screen.
     Reading `scene` here made the control say "Property" over a photograph of a car — the same
     mismatch SETTLE exists to prevent for the boxes. */
  const dest = onPick ? SCENE_DEST[scenes[Math.min(shown, scenes.length - 1)].id] : null;

  return (
    <div className="cn-scanbox">
    <div className={`cn-scan${dest ? " joined" : ""}`} aria-hidden="true">
      {/* all scenes are mounted so a switch never flashes an unloaded image */}
      {scenes.map((s, i) => (
        <img key={s.id} className={`cn-scanimg${i === scene ? " on" : ""}`}
          src={IMG(s.img)} alt="" draggable="false" decoding="async"
          /* five plates are mounted at once on the hero; the one on screen must not queue
             behind the four nobody can see yet */
          fetchpriority={i === 0 ? "high" : "low"} />
      ))}
      <span className="cn-scangrid" />
      {!still && <span className="cn-scanline" />}

      {/* viewfinder brackets on the frame itself (the boxes carry no corners of their own) */}
      <span className="cn-scanframe"><i /><i /><i /><i /></span>

      {current.dets.map((d) => (
        <div
          key={`${current.id}-${d.id}`}
          className={`cn-det${d.warn ? " warn" : ""}${d.flip ? " flip" : ""}${d.below ? " below" : ""}${live.includes(d.id) ? " on" : ""}`}
          style={{ left: `${d.x}%`, top: `${d.y}%`, width: `${d.w}%`, height: `${d.h}%` }}
        >
          <span className="cn-detlabel">
            {t(d.label)} <Confidence value={d.conf} still={still} />
          </span>
        </div>
      ))}

      {/* status strip — the count rises with the boxes, so it is never ahead of the picture */}
      <span className="cn-scanstrip">
        <span className="l">
          {!still && <i className="cn-dot" />}
          {t("co_det_live")}
        </span>
        <span className="r">{t("co_det_found", { n: live.length })}</span>
      </span>
    </div>

    {/* The way out of the picture. Deliberately OUTSIDE the aria-hidden panel above: a focusable
        control inside an aria-hidden subtree is reachable by tab but invisible to a screen
        reader, which is worse than not having it. Keyed on the scene so the label animates with
        the plate rather than swapping under it. */}
    {dest && (
      <button type="button" className="cn-scanpick" onClick={() => onPick(dest.v, dest.u)}>
        <span className="cn-scanpicktx">{t(dest.label)}</span>
        <Chev />
      </button>
    )}
    </div>
  );
};

export default DetectionOverlay;
